// Placeholder origin used only to parse the path. `.invalid` (RFC 6761) is never a
// real host, so any input that lands on a different origin must be cross-origin. We
// return just the path, so this host never leaves the function.
const PARSE_ORIGIN = 'https://lookout.invalid';

/**
 * `return_to` is persisted verbatim on `oauth_login_state` by an unauthenticated request, so a cap
 * bounds the row. Every real local path in the app, including the MCP authorize round-trip and the
 * sandbox join link with its capped campaign query, is far shorter.
 *
 * The cap is applied twice: once to the raw input, and again to the normalised path. Parsing can
 * grow a value, because `URL` percent-encodes what the input left bare — 2000 raw `"` become 6000
 * characters of `%22`. Checking only the input would let a crafted request store a row three times
 * the limit, which the callback's own `safeReturnTo` would then reject, stranding that login.
 */
const MAX_LENGTH = 2048;

/**
 * Turn a user-supplied `return_to` value into a safe same-origin local path, or null
 * if we shouldn't follow it.
 *
 * We parse with the `URL` API and check the result stays on our origin, instead of
 * checking the raw string. The browser does the same when it follows a redirect, and
 * the two can disagree: `/\t//evil.com` passes a `startsWith('//')` check, but the
 * browser strips the leading TAB into `//evil.com` and navigates to `https://evil.com`
 * — an open redirect (CWE-601). Parsing makes us judge the input the way the browser
 * will. We return only the normalized path, dropping any host the input tried to add.
 *
 * An open redirect can also leak an OAuth `code` to an attacker's site, so run every
 * `return_to` value through this — never trust the raw query or cookie.
 */
export function safeReturnTo(input: string | undefined | null): string | null {
  if (typeof input !== 'string' || input.length === 0 || input.length > MAX_LENGTH) return null;
  // CR/LF would let the value inject extra response headers.
  if (input.includes('\r') || input.includes('\n')) return null;
  // A local path must start with `/`. `//evil.com` also passes here but is rejected
  // later by the origin and `//` checks.
  if (!input.startsWith('/')) return null;
  let url: URL;
  try {
    url = new URL(input, PARSE_ORIGIN);
  } catch {
    return null;
  }
  // Anything that parses to a different origin is a redirect off our site.
  if (url.origin !== PARSE_ORIGIN) return null;
  const path = url.pathname + url.search + url.hash;
  // A dot-segment like `/.//evil.com` keeps our origin but normalizes the path to
  // `//evil.com`, which is protocol-relative (cross-origin) when used as a Location.
  if (path.startsWith('//')) return null;
  // Re-check after normalising: percent-encoding can grow the value well past the raw-input check.
  if (path.length > MAX_LENGTH) return null;
  return path;
}
